import React, { ReactNode, useState } from 'react';
import { Rnd } from 'react-rnd';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import { ContactData } from '../../api/ContactListening/schema';
import { ContactTimelineDisplay } from './components/ContactTimelineDisplay';
import Column from '@amzn/meridian/column';
import { useIntl } from 'react-intl';
import { Feature, isFeatureEnabled } from '../../config/FeaturesConfig';
import closeMedium from '@amzn/meridian-tokens/base/icon/close-medium';
import expand from '@amzn/meridian-tokens/base/icon/expand';
import { MERIDIAN_COLORS } from '../../Util';
import Icon from '@amzn/meridian/icon';
import Heading from '@amzn/meridian/heading';
import Expander from '@amzn/meridian/expander';

export interface ContactListeningMultitaskingBoxProps {
  contactId: string;
  onClose: any;
  column: ReactNode;
  headerMsg: string;
  loadingState: string;
  contactListeningData: ContactData;
  openContactTranscript: boolean;
  setOpenContactTranscript: React.Dispatch<React.SetStateAction<boolean>>;
}

const ContactListeningMultitaskingBox: React.FC<
  ContactListeningMultitaskingBoxProps
> = ({
  contactId,
  onClose,
  column,
  headerMsg,
  loadingState,
  contactListeningData,
  openContactTranscript,
  setOpenContactTranscript,
}: ContactListeningMultitaskingBoxProps): React.ReactElement => {
  const intl = useIntl();
  const [openContactTimeline, setOpenContactTimeline] = useState(false);
  const enableContactTimeline = isFeatureEnabled(Feature.CONTACT_TIMELINE);
  const [posX, setPosX] = useState(
    Math.floor(window.innerWidth * 0.2) +
      (Math.floor(window.innerWidth * 0.2) % 2)
  );
  const [posY, setPosY] = useState(
    Math.floor(window.scrollY / 4) + (Math.floor(window.scrollY / 4) % 2)
  );

  return (
    <Rnd
      default={{
        x: posX,
        y: posY,
        width: Math.round(window.innerWidth * 0.75),
        height: 1000,
      }}
      position={{
        x: posX,
        y: posY,
      }}
      onResizeStop={(e, direction, ref, delta, position) => {
        setPosX(Math.floor(position.x) + (Math.floor(position.x) % 2));
        setPosY(Math.floor(position.y) + (Math.floor(position.y) % 2));
      }}
      onDragStop={(e, d) => {
        setPosX(Math.floor(d.x) + (Math.floor(d.x) % 2));
        setPosY(Math.floor(d.y) + (Math.floor(d.y) % 2));
      }}
      enableResizing={true}
      minWidth='880px'
      maxWidth='880px'
      minHeight={700}
      resizeHandleComponent={{
        bottomRight: (
          <div
            style={{
              position: 'absolute',
              right: '1em',
              bottom: '1em',
              transform: 'rotate(90deg)',
            }}
          >
            <Icon tokens={expand} />
          </div>
        ),
      }}
      style={{
        zIndex: 50,
        border: `0.1rem solid ${MERIDIAN_COLORS.MERIDIAN_COLOR_GRAY_800}`,
        backgroundColor: `${MERIDIAN_COLORS.MERIDIAN_COLOR_GRAY_0}`,
        borderRadius: '5px',
        boxShadow: '2px',
        overflowY: 'scroll',
        overflowX: 'hidden',
        WebkitFontSmoothing: 'subpixel-antialiased',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        {contactId && (
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              paddingBottom: '1em',
              paddingTop: '1em',
              marginBottom: '1em',
              backgroundColor: MERIDIAN_COLORS.MERIDIAN_COLOR_GRAY_800,
            }}
          >
            <Column minWidth='750px' maxWidth='750px' spacing='300'>
              <Row width='100%' widths='fill'>
                <Row>
                  <Heading id='tablist-heading' level={5}>
                    <span
                      style={{ color: MERIDIAN_COLORS.MERIDIAN_COLOR_GRAY_0 }}
                      data-cy='CL-Header'
                    >
                      {headerMsg}
                    </span>
                  </Heading>
                </Row>
                <Row alignmentHorizontal='end'>
                  <Button type='icon' circular={true} onClick={onClose}>
                    <span
                      style={{ color: MERIDIAN_COLORS.MERIDIAN_COLOR_GRAY_0 }}
                    >
                      <Icon tokens={closeMedium} />
                    </span>
                  </Button>
                </Row>
              </Row>
            </Column>
          </div>
        )}

        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            paddingBottom: '1em',
            paddingTop: '1em',
            marginBottom: '1em',
          }}
        >
          <Column
            minWidth='750px'
            maxWidth='750px'
            spacing='300'
            data-cy-opentranscript={openContactTranscript}
            data-cy-opentimeline={openContactTimeline}
          >
            <Row width='100%' widths='fill'>
              <Expander
                open={openContactTranscript}
                onChange={setOpenContactTranscript}
                title={intl.formatMessage({
                  id: 'cs_perfportal_contact_listening_contact_transcript_heading',
                  defaultMessage: 'Contact Transcript',
                  description:
                    'Expander for Contact media like recording and chat.',
                })}
                type='section'
              >
                {column}
              </Expander>
            </Row>
            {enableContactTimeline && (
              <Row
                width='100%'
                widths='fill'
                data-cy-timeline={enableContactTimeline}
              >
                <Expander
                  open={openContactTimeline}
                  onChange={setOpenContactTimeline}
                  title={`${intl.formatMessage({
                    id: 'cs_perfportal_contact_timeline_title',
                    defaultMessage: 'Contact Timeline',
                  })}`}
                  type='section'
                >
                  <>
                    <ContactTimelineDisplay
                      loadingState={loadingState}
                      contactId={contactId}
                      contactListeningData={contactListeningData}
                    />
                  </>
                </Expander>
              </Row>
            )}
          </Column>
        </div>
      </div>
    </Rnd>
  );
};
export default ContactListeningMultitaskingBox;
